import React, { useEffect, useRef } from "react";

// The whole value of a sequence is the ORDER, so it gets transport rather than
// being drawn all at once. Play stops at the end: this is a text to be read,
// not an ambient animation.
// Roughly 900ms a segment for the Flash's nine. The Serpent has twenty-two, so
// it gets a faster default or it runs over twenty seconds — and a speed control
// rather than a number guessed for everyone.
const BASE_MS = 900;
const SPEEDS = [
  ["0.5", "0.5x"],
  ["1", "1x"],
  ["2", "2x"]
];

export default function SequenceTransport({
  overlay,
  steps,
  step,
  playing,
  fixed,
  speed,
  onSpeed,
  onStep,
  onTogglePlay
}) {
  const total = steps.length;
  const timer = useRef(null);
  // Twenty-two segments at 900ms is twenty seconds, so a long sequence paces
  // itself up a little.
  const tick = (BASE_MS * (total > 12 ? 0.6 : 1)) / Number(speed || 1);

  useEffect(() => {
    if (!playing) return undefined;
    // Stop at the end. An ambient loop on a study diagram is a distraction.
    if (step >= total) {
      onTogglePlay(false);
      return undefined;
    }
    timer.current = setTimeout(() => onStep(step + 1), tick);
    return () => clearTimeout(timer.current);
  }, [playing, step, total, tick, onStep, onTogglePlay]);

  if (!overlay || total === 0) return null;

  const current = steps[step - 1];
  const label = current ? current.label : "";

  return (
    <div
      className={fixed ? "transport transport--fixed" : "transport"}
      data-testid="sequence-transport"
    >
      <div className="transport__controls">
        <button
          type="button"
          className="transport__button"
          onClick={() => onStep(step - 1)}
          disabled={step <= 1}
          aria-label="Previous step"
        >
          ‹
        </button>
        <button
          type="button"
          className="transport__button transport__button--play"
          onClick={() => onTogglePlay(!playing)}
          disabled={step >= total && !playing}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <button
          type="button"
          className="transport__button"
          onClick={() => onStep(step + 1)}
          disabled={step >= total}
          aria-label="Next step"
        >
          ›
        </button>
        <p className="transport__position" data-testid="transport-position">
          {step} of {total}
        </p>

        <label className="transport__speed">
          <span className="visually-hidden">Speed</span>
          <select
            value={String(speed)}
            onChange={(event) => onSpeed(event.target.value)}
            data-testid="transport-speed"
          >
            {SPEEDS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Kept for screen readers — the visual emphasis is invisible to them —
          but visually hidden: the same sentence is already in the right panel,
          and it appeared three times on screen. */}
      <p
        className="visually-hidden"
        aria-live="polite"
        data-testid="transport-announce"
      >
        {`Step ${step} of ${total}. ${label}`}
      </p>
    </div>
  );
}
